import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../../../assets/css/modal.css';
import { useCssValueTemplateStore } from '../../../../store/useCssValueTemplateStore';
import { useLoginStore } from '../../../../store/useLogin';
import { CssValue, CssValueDetail } from '../../../../type/cssTemplate';
import StyleListPanel from '../../../modal/style/StyleListPanel';
import { toast } from 'react-toastify';

interface ITemplateStyleModal {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  productId?: string;
  onSaveSuccess?: () => void;
}

// 스타일 모달: 좌측 카테고리(categoryName), 우측 contents + iframe 미리보기
const TemplateStyleModal: React.FC<ITemplateStyleModal> = ({
  isOpen,
  onClose,
  title = '스타일 선택',
  productId = '',
  onSaveSuccess,
}) => {
  const { values, loading, getCssValues, addPendingCategory, pendingCreates, savePending, clearPending, useCssContent: submitCssContent } = useCssValueTemplateStore();
  const { role } = useLoginStore();
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const [selectedByCategory, setSelectedByCategory] = useState<Record<string, string>>({});
  const [initialSelectedByCategory, setInitialSelectedByCategory] = useState<Record<string, string>>({});
  const [hasInitializedSelections, setHasInitializedSelections] = useState(false);
  // 어드민 입력 상태
  const [newCategory, setNewCategory] = useState('');
  const lastFetchedSignatureRef = useRef('');
  const dataLoadedRef = useRef(false);
  const lastLoadedProductIdRef = useRef<string | null>(null);

  // 모달 열릴 때 데이터 로드
  useEffect(() => {
    if (!isOpen) {
      setSelectedCategoryName(null);
      setSelectedByCategory({});
      setNewCategory('');
      clearPending();
      setInitialSelectedByCategory({});
      lastFetchedSignatureRef.current = '';
      dataLoadedRef.current = false;
      lastLoadedProductIdRef.current = null;
      setHasInitializedSelections(false);
      return;
    }

    if (!productId) return;

    const isSameProductLoaded = dataLoadedRef.current && lastLoadedProductIdRef.current === productId;
    if (isSameProductLoaded) {
      return;
    }

    setHasInitializedSelections(false);

    getCssValues(productId).then((list) => {
      if (!selectedCategoryName && list.length > 0) {
        setSelectedCategoryName(list[0].categoryName);
      }

      let computedNext: Record<string, string> = {};

      setSelectedByCategory(prev => {
        const allowed = new Set(list.map((v: CssValue) => v.categoryName));
        const next: Record<string, string> = {};
        Object.entries(prev).forEach(([k, v]) => {
          if (allowed.has(k) && v) next[k] = v;
        });

        list.forEach((v) => {
          if (!next[v.categoryName]) {
            const usedItem = (v.contents || []).find((c: any) => (c as any).used || (c as any).isUsed);
            if (usedItem) {
              next[v.categoryName] = usedItem.cssContentId || usedItem.cssName;
            }
          }
        });

        computedNext = next;
        return next;
      });

      const signature = JSON.stringify(list.map(item => ({
        categoryName: item.categoryName,
        contents: (item.contents || []).map(content => ({
          id: content.cssContentId || content.cssName,
          used: (content as any).isUsed || (content as any).used
        }))
      })));

      if (lastFetchedSignatureRef.current !== signature) {
        lastFetchedSignatureRef.current = signature;
        setInitialSelectedByCategory(computedNext);
      }

      dataLoadedRef.current = true;
      lastLoadedProductIdRef.current = productId;
      setHasInitializedSelections(true);
    }).catch(() => {
      dataLoadedRef.current = false;
      lastLoadedProductIdRef.current = null;
    });
  }, [isOpen, productId, getCssValues, clearPending, selectedCategoryName]);

  const categories = useMemo(() => {
    const base = new Set((values || []).map(v => v.categoryName));
    (pendingCreates || []).forEach(c => base.add(c.categoryName));
    return Array.from(base);
  }, [values, pendingCreates]);
  const duplicateCategoryMessage = useMemo(() => {
    const trimmed = newCategory.trim();
    if (!trimmed) return '';
    const lower = trimmed.toLowerCase();
    const exists = categories.some((name) => (name || '').trim().toLowerCase() === lower);
    return exists ? '이미 존재하는 카테고리입니다.' : '';
  }, [newCategory, categories]);
  const currentContents: CssValueDetail[] = useMemo(() => {
    const base = [...((values || []).find(v => v.categoryName === selectedCategoryName)?.contents || [])];
    const pendingEntry = (pendingCreates || []).find(v => v.categoryName === selectedCategoryName);
    const pendingContents = pendingEntry?.contents || [];

    if (pendingContents.length === 0) {
      return base;
    }

    const appended: CssValueDetail[] = [];
    const resolveDetail = (pending: any, fallback?: CssValueDetail): CssValueDetail => {
      const uploadMeta = pending.uploadFileId
        ? { id: pending.uploadFileId, fileFullUrl: pending.previewUrl }
        : (pending.previewUrl ? { fileFullUrl: pending.previewUrl } : fallback?.uploadFileId);
      const merged: CssValueDetail = {
        cssContentId: pending.cssContentId || fallback?.cssContentId || '',
        cssName: pending.cssName ?? fallback?.cssName ?? '',
        cssValue: pending.cssValue ?? fallback?.cssValue ?? '',
        isUsed: typeof pending.isUsed === 'boolean' ? pending.isUsed : (fallback?.isUsed ?? false),
        originCssName: pending.originCssName ?? pendingEntry?.originCssName ?? fallback?.originCssName,
        uploadFileId: uploadMeta
      } as CssValueDetail;
      return merged;
    };

    pendingContents.forEach((pending: any) => {
      if (pending.cssContentId) {
        const idx = base.findIndex(item => item.cssContentId === pending.cssContentId);
        if (idx >= 0) {
          base[idx] = { ...base[idx], ...resolveDetail(pending, base[idx]) };
        } else {
          appended.push(resolveDetail(pending));
        }
      } else {
        appended.push(resolveDetail(pending));
      }
    });

    return [...base, ...appended];
  }, [values, pendingCreates, selectedCategoryName]);

  const selectedIds = useMemo(() => Object.values(selectedByCategory).filter(Boolean), [selectedByCategory]);
  const currentSelectedId = selectedCategoryName ? selectedByCategory[selectedCategoryName] : undefined;
  const hasPendingToSave = role === 'ROLE_ADMIN' && (pendingCreates?.length ?? 0) > 0;
  const hasSelectionChanges = useMemo(() => {
    console.log('hasSelectionChanges', hasInitializedSelections, selectedByCategory, initialSelectedByCategory);
    if (!hasInitializedSelections) {
      return false;
    }
    const combinedKeys = Array.from(new Set([
      ...Object.keys(selectedByCategory),
      ...Object.keys(initialSelectedByCategory),
    ]));
    return combinedKeys.some((key) => {
      const currentValue = selectedByCategory[key] || '';
      const baselineValue = initialSelectedByCategory[key] || '';
      return currentValue !== baselineValue;
    });
  }, [hasInitializedSelections, selectedByCategory, initialSelectedByCategory]);
  const hasPendingChanges = hasPendingToSave || hasSelectionChanges;
  const showPendingNotice = hasInitializedSelections && hasPendingChanges;
  const isSaveDisabled = !hasPendingChanges || !productId;

  const handleSave = async () => {
    if (isSaveDisabled) return;

    try {
      let savedPending = false;
      let savedSelection = false;

      if (hasPendingToSave) {
        await savePending();
        if (productId) await getCssValues(productId);
        savedPending = true;
      }

      if (hasSelectionChanges && selectedIds.length > 0 && productId) {
        await submitCssContent(productId, selectedIds);
        savedSelection = true;
      }

      if (savedSelection && savedPending) {
        toast.success('신규/수정 항목과 선택한 스타일이 반영되었습니다.');
      } else if (savedSelection) {
        toast.success('선택한 스타일이 반영되었습니다.');
      } else if (savedPending) {
        toast.success('신규/수정 항목이 저장되었습니다.');
      }

      if (savedSelection || savedPending) {
        onSaveSuccess?.();
      }

      if (savedSelection) {
        onClose();
      }
    } catch (error) {
      console.error('저장 처리 중 오류:', error);
      toast.error('저장에 실패했습니다.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="template-modal-overlay" onClick={onClose}>
      <div className="template-modal-container relative max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="template-modal-header">
          <div>
            <h2>{title}</h2>
          </div>
          <button className="template-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* 본문 */}
        <div className="template-modal-filters flex flex-col md:flex-row gap-4 p-3.5 flex-1 overflow-y-auto">
          {/* 왼쪽: 카테고리 목록 */}
          <div className="w-full md:w-1/5 overflow-y-auto pr-4 md:border-r border-gray-200">
            <h3 className="text-base font-semibold text-gray-700 mb-3">카테고리</h3>
            {loading && <div className="text-xs text-gray-500">불러오는 중...</div>}
            <div className="grid grid-cols-1 gap-2">
              {(!categories || categories.length === 0) && !loading && (
                <div className="text-xs text-gray-500">표시할 카테고리가 없습니다.</div>
              )}
              {categories && categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategoryName(cat)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors text-sm font-semibold ${
                    selectedCategoryName === cat 
                      ? 'bg-blue-100 border-blue-200 text-blue-800' 
                      : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
              {role === 'ROLE_ADMIN' && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-500 mb-1.5">카테고리 추가</div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-1.5">
                    <input
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="새 카테고리명"
                      className={`w-full border rounded-md px-2 py-1.5 text-xs ${duplicateCategoryMessage ? 'border-red-400' : 'border-gray-300'}`}
                    />
                    <button
                      onClick={async () => {
                        const trimmed = newCategory.trim();
                        if (!trimmed || duplicateCategoryMessage) return;
                        addPendingCategory(trimmed, '');
                        if (!selectedCategoryName) setSelectedCategoryName(trimmed);
                        setNewCategory('');
                      }}
                      className="px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-md border border-gray-800 hover:bg-gray-700"
                    >추가</button>
                  </div>
                  {duplicateCategoryMessage && (
                    <div className="text-[11px] text-red-500 mt-1">{duplicateCategoryMessage}</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 오른쪽: contents 패널 */}
          <div className="flex-1">
            <StyleListPanel
              categoryName={selectedCategoryName}
              contents={currentContents}
              productId={productId}
              selectedIds={selectedIds}
              currentSelectedId={currentSelectedId}
              onSelect={(id) => {
                if (!selectedCategoryName) return;
                setSelectedByCategory(prev => ({ ...prev, [selectedCategoryName]: id }));
              }}
            />
          </div>
        </div>

        {/* 푸터 */}
        <div className="template-modal-footer p-3 border-t border-gray-200">
          <div className="flex flex-col gap-2 w-full">
            {hasPendingToSave && (
              <div className="text-xs text-gray-500 text-center sm:text-left">
                보류 카테고리: {pendingCreates.length}개, 보류 항목: {pendingCreates.reduce((a,c)=>a+(c.contents?.length||0),0)}개
              </div>
            )}
            <div className="flex items-center justify-end gap-2 w-full">
              <button className="template-btn-cancel" onClick={onClose}>닫기</button>
              <div className="relative ml-2">
                {showPendingNotice && (
                  <div className="pointer-events-none absolute bottom-[125%] left-1/2 z-50 w-max -translate-x-1/2 rounded-lg bg-[#d32f2f] px-4 py-[10px] text-[15px] font-bold text-white shadow-[0_4px_12px_rgba(0,0,0,0.25)]">
                    저장해야 변경사항이 반영됩니다!
                    <div className="absolute -bottom-2 left-1/2 h-0 w-0 -translate-x-1/2 border-x-[8px] border-x-transparent border-t-[8px] border-t-[#d32f2f]" />
                  </div>
                )}
                <button
                  className="template-btn-save rounded-lg bg-blue-600 px-6 py-[10px] text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                  disabled={isSaveDisabled}
                  onClick={handleSave}
                >저장하기</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateStyleModal;
